/**
 * Dates, handled honestly.
 *
 * The API serialises `LocalDateTime` — "2026-08-05T14:30:00" with no zone. Those
 * values are Cairo wall-clock times: the backend runs Hibernate with
 * `hibernate.jdbc.time_zone=Africa/Cairo`, and the drivers, passengers and trips
 * they describe all happen there.
 *
 * Handing such a string to `new Date()` makes the browser read it in *its own*
 * timezone, so the same trip would show 14:30 in Cairo and 12:30 in London. The
 * dashboard therefore renders these values as the Cairo wall-clock times they
 * are, wherever the admin is sitting, and labels the zone so there is no doubt.
 */

export const OPERATIONS_TIME_ZONE = 'Africa/Cairo'
export const OPERATIONS_TIME_ZONE_LABEL = 'Cairo'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

export interface DateTimeParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/

/** Reads the naive date-time the API sends, tolerating a missing time or seconds. */
export function parseApiDateTime(value: string | null | undefined): DateTimeParts | null {
  if (!value) return null

  const match = DATE_TIME_PATTERN.exec(value.trim())
  if (!match) return null

  const parts: DateTimeParts = {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? '0'),
    minute: Number(match[5] ?? '0'),
    second: Number(match[6] ?? '0'),
  }
  return Number.isFinite(parts.year) && parts.month >= 1 && parts.month <= 12 ? parts : null
}

/** How far the zone is from UTC at a given instant, DST included. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  const parts = formatter.formatToParts(new Date(utcMs))
  const lookup = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type)
    return found ? Number(found.value) : 0
  }

  // `formatToParts` renders midnight as hour 24 in some engines.
  const hour = lookup('hour') % 24

  const asUtc = Date.UTC(lookup('year'), lookup('month') - 1, lookup('day'), hour, lookup('minute'), lookup('second'))
  return asUtc - utcMs
}

/**
 * Converts a Cairo wall-clock reading into a real instant.
 *
 * Applied twice: the first pass uses the offset at the wrong instant, which is
 * off by an hour across a DST boundary — Egypt observes DST again since 2023.
 */
export function operationsTimeToInstant(parts: DateTimeParts): number {
  const naiveUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
  let instant = naiveUtc - zoneOffsetMs(naiveUtc, OPERATIONS_TIME_ZONE)
  instant = naiveUtc - zoneOffsetMs(instant, OPERATIONS_TIME_ZONE)
  return instant
}

/** The current moment as a Cairo wall-clock reading. */
export function operationsNow(): DateTimeParts {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: OPERATIONS_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })

  const parts = formatter.formatToParts(new Date())
  const lookup = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type)
    return found ? Number(found.value) : 0
  }

  return {
    year: lookup('year'),
    month: lookup('month'),
    day: lookup('day'),
    hour: lookup('hour') % 24,
    minute: lookup('minute'),
    second: lookup('second'),
  }
}

/**
 * Whole minutes since an operational (Cairo) reading, negative while it is still
 * ahead. Null when there is no readable time.
 *
 * Wall clock in, real instants out: the reading is resolved to the moment it
 * names in Cairo before being compared to now, so "this trip was due to start
 * twenty minutes ago" is the same answer for an admin in London as for one in
 * Maadi. Comparing the raw string to a local `Date` would be off by the
 * viewer's own offset, which is how a trip becomes due two hours early.
 */
export function minutesSinceOperationsTime(
  value: string | null | undefined,
  now: number = Date.now(),
): number | null {
  const parts = parseApiDateTime(value)
  if (!parts) return null
  return Math.floor((now - operationsTimeToInstant(parts)) / 60_000)
}

const pad = (value: number): string => String(value).padStart(2, '0')

export function formatDate(value: string | null | undefined, fallback = '—'): string {
  const parts = parseApiDateTime(value)
  if (!parts) return fallback
  return `${pad(parts.day)} ${MONTHS[parts.month - 1]} ${parts.year}`
}

export function formatTime(value: string | null | undefined, fallback = '—'): string {
  const parts = parseApiDateTime(value)
  if (!parts) return fallback
  return `${pad(parts.hour)}:${pad(parts.minute)}`
}

export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  const parts = parseApiDateTime(value)
  if (!parts) return fallback
  return `${formatDate(value)}, ${formatTime(value)}`
}

/** Short form for dense table cells: "05 Aug · 14:30". */
export function formatDateTimeCompact(value: string | null | undefined, fallback = '—'): string {
  const parts = parseApiDateTime(value)
  if (!parts) return fallback
  return `${pad(parts.day)} ${MONTHS[parts.month - 1]} · ${pad(parts.hour)}:${pad(parts.minute)}`
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
]

export function formatRelative(value: string | null | undefined, fallback = '—'): string {
  const parts = parseApiDateTime(value)
  if (!parts) return fallback

  const difference = operationsTimeToInstant(parts) - Date.now()
  const absolute = Math.abs(difference)

  if (absolute < 60 * 1000) return 'just now'

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  for (const [unit, milliseconds] of RELATIVE_UNITS) {
    if (absolute >= milliseconds) {
      return formatter.format(Math.round(difference / milliseconds), unit)
    }
  }
  return 'just now'
}

/** Value for `<input type="datetime-local">`, which wants naive local text. */
export function toDateTimeLocalInput(value: string | null | undefined): string {
  const parts = parseApiDateTime(value)
  if (!parts) return ''
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
}

/**
 * Turns a `datetime-local` value back into what the API expects. The admin typed
 * an operational (Cairo) time and that is exactly what is sent — no conversion,
 * because the backend stores the same wall clock.
 */
export function fromDateTimeLocalInput(value: string): string {
  const parts = parseApiDateTime(value)
  if (!parts) return ''
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`
}

/**
 * Minutes from one `datetime-local` value to another, or null if either is
 * unreadable.
 *
 * Wall-clock arithmetic on purpose. "This trip moved two hours later" is a
 * statement about the clock the driver and passenger read, so it must stay two
 * hours across a DST boundary rather than becoming one or three.
 */
export function dateTimeLocalDeltaMinutes(from: string, to: string): number | null {
  const start = parseApiDateTime(from)
  const end = parseApiDateTime(to)
  if (!start || !end) return null
  return Math.round((asWallClockMs(end) - asWallClockMs(start)) / 60_000)
}

/** Shifts a `datetime-local` value by a number of minutes on the wall clock. */
export function addMinutesToDateTimeLocal(value: string, minutes: number): string {
  const parts = parseApiDateTime(value)
  if (!parts) return value

  const shifted = new Date(asWallClockMs(parts) + minutes * 60_000)
  return (
    `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}` +
    `T${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`
  )
}

/**
 * The reading treated as if it were UTC, purely so date arithmetic can be done
 * on it. Never an instant — see {@link operationsTimeToInstant} for that.
 */
function asWallClockMs(parts: DateTimeParts): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
}

/** Start of a day, for a "from" date filter. */
export function startOfDayParam(date: string): string {
  return date ? `${date}T00:00:00` : ''
}

/** End of a day, so a "to" filter includes everything that happened that day. */
export function endOfDayParam(date: string): string {
  return date ? `${date}T23:59:59` : ''
}

/** ISO calendar date ("2026-08-05") for `<input type="date">`. */
export function toDateInput(parts: DateTimeParts): string {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

/** Labels a chart tick as "05 Aug". */
export function formatChartDate(value: string): string {
  const parts = parseApiDateTime(value)
  if (!parts) return value
  return `${pad(parts.day)} ${MONTHS[parts.month - 1]}`
}
